import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import Eyebrow from '../components/ui/Eyebrow';
import HeroBackground from '../components/ui/HeroBackground';
import ManagerAvatar from '../components/ui/ManagerAvatar';
import { CastFigure, Depth, Reveal } from '../motion';
import { loadBureau } from '../utils/bureau';
import { managerSlug } from '../utils/slug';

function signed(value) {
  if (!Number.isFinite(value)) return '—';
  return value > 0 ? `+${value}` : String(value);
}

function ManagerLink({ name, children }) {
  return (
    <Link to={`/managers/${managerSlug(name)}`} className="hover:text-accent-gold">
      {children || name}
    </Link>
  );
}

function Department({ tag, title, dek, children }) {
  return (
    <section className="px-6 md:px-14 py-12 md:py-16">
      <div className="font-display tracking-[0.35em] text-[11px] text-accent-gold font-bold uppercase">{tag}</div>
      <h2 className="mt-2 font-display font-black uppercase leading-none text-4xl md:text-5xl">{title}</h2>
      {dek && <p className="mt-4 max-w-2xl font-body italic text-ink-secondary">{dek}</p>}
      <div className="mt-8">{children}</div>
    </section>
  );
}

export default function Bureau() {
  const [bureau, setBureau] = useState(null);

  useEffect(() => {
    loadBureau().then(setBureau).catch(() => setBureau(null));
  }, []);

  if (!bureau) {
    return (
      <div className="px-6 md:px-14 py-20">
        <Eyebrow>The Bureau</Eyebrow>
        <h1 className="text-display-md font-black tracking-normal mt-3.5">The Bureau</h1>
        <p className="mt-4 font-body italic text-ink-secondary">Loading the ledger.</p>
      </div>
    );
  }

  const { luck, sacko, curse, awards } = bureau;
  const luckBoard = luck.careers.slice().sort((a, b) => a.luckWins - b.luckWins);

  return (
    <>
      <HeroBackground backdrop="court" variant="red" className="px-6 md:px-14 pt-10 pb-14">
        <Eyebrow color="red">Vol XIV · Department of losing</Eyebrow>
        <h1 className="mt-4 font-display font-black uppercase leading-[0.86] tracking-normal"
            style={{ fontSize: 'clamp(48px, 8vw, 104px)' }}>
          The Bureau
          <span className="block text-accent-red">keeps the receipts.</span>
        </h1>
        <p className="mt-6 max-w-[48ch] font-body italic text-xl md:text-2xl text-ink-secondary leading-snug">
          Luck, last place, and the regular-season curse — computed from 13 seasons and 875
          regular-season games. Awards that are not rings. Nobody gets a trophy for being good.
        </p>
        <div className="relative mt-8 max-w-3xl">
          <style>{`
            @keyframes bureau-spotlight-sweep {
              0%, 100% { transform: translateX(-50%) rotate(-3deg); }
              50% { transform: translateX(-50%) rotate(3deg); }
            }
            .bureau-spotlight-cone {
              animation: bureau-spotlight-sweep 24s linear infinite;
              background: conic-gradient(from 205deg at 50% 0%, transparent 0deg 39deg, rgba(251, 191, 36, 0.16) 49deg, transparent 59deg 360deg);
              transform-origin: 50% 0%;
            }
            @media (prefers-reduced-motion: reduce) {
              .bureau-spotlight-cone {
                animation: none;
                transform: translateX(-50%);
              }
            }
          `}</style>
          <div className="bureau-spotlight-cone pointer-events-none absolute -top-8 bottom-[-2rem] left-1/2 w-[120%]" aria-hidden="true" />
          <Depth className="relative grid grid-cols-2 gap-3 md:grid-cols-4" strength={0.3}>
            <HeroChip name={luck.unluckiest.manager} value={luck.unluckiest.manager} label="Unluckiest" />
            <HeroChip name={luck.luckiest.manager} value={luck.luckiest.manager} label="Luckiest" />
            <HeroChip name={sacko.king.manager} value={`${sacko.king.count}× ${sacko.king.manager}`} label="Sacko king" />
            <HeroChip name={curse.cursedKing.manager} value={curse.cursedKing.manager} label="Curse king" />
          </Depth>
        </div>
      </HeroBackground>

      <Department
        tag="Luck Tribunal"
        title="Play the field."
        dek="Every week, your score is matched against every other score, not just the opponent Yahoo assigned. Expected wins are that field record. Luck is actual wins minus expected wins. A brutal loss is losing to the only team that scored more than you."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <Reveal as="article" className="bg-bg-surface border-t-[3px] border-accent-red p-5" data-testid="bureau-unluckiest">
            <div className="text-[10px] font-display tracking-[0.22em] uppercase text-accent-red font-bold">Unluckiest</div>
            <div className="mt-2 flex items-center gap-3">
              <ManagerAvatar name={luck.unluckiest.manager} size="md" />
              <div>
                <ManagerLink name={luck.unluckiest.manager}>
                  <div className="font-display font-black uppercase text-2xl leading-none">{luck.unluckiest.manager}</div>
                </ManagerLink>
                <div className="mt-1 font-mono text-accent-red text-xl">{signed(luck.unluckiest.luckWins)}</div>
              </div>
            </div>
            <p className="mt-3 font-body italic text-sm text-ink-secondary">
              {luck.unluckiest.actualWins} actual wins, {luck.unluckiest.expectedWins} expected. The schedule has been a hobby.
            </p>
          </Reveal>
          <Reveal as="article" className="bg-bg-surface border-t-[3px] border-accent-gold p-5" data-testid="bureau-luckiest" delay={100}>
            <div className="text-[10px] font-display tracking-[0.22em] uppercase text-accent-gold font-bold">Luckiest</div>
            <div className="mt-2 flex items-center gap-3">
              <ManagerAvatar name={luck.luckiest.manager} size="md" />
              <div>
                <ManagerLink name={luck.luckiest.manager}>
                  <div className="font-display font-black uppercase text-2xl leading-none">{luck.luckiest.manager}</div>
                </ManagerLink>
                <div className="mt-1 font-mono text-accent-gold text-xl">{signed(luck.luckiest.luckWins)}</div>
              </div>
            </div>
            <p className="mt-3 font-body italic text-sm text-ink-secondary">
              {luck.luckiest.actualWins} actual, {luck.luckiest.expectedWins} expected. The Bureau does not allege witchcraft.
            </p>
          </Reveal>
        </div>
        <Reveal className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left" data-testid="bureau-luck-table">
            <thead>
              <tr className="font-display text-[10px] tracking-[0.2em] uppercase text-ink-muted border-b border-border">
                <th className="py-2 pr-3">Manager</th>
                <th className="py-2 pr-3">Games</th>
                <th className="py-2 pr-3">Actual</th>
                <th className="py-2 pr-3">Expected</th>
                <th className="py-2 pr-3">Luck</th>
                <th className="py-2 pr-3">Lucky W</th>
                <th className="py-2">Brutal L</th>
              </tr>
            </thead>
            <tbody>
              {luckBoard.map((row) => (
                <tr key={row.manager} className="border-b border-border/60 font-mono text-sm">
                  <td className="py-2 pr-3 font-display font-black uppercase tracking-normal">
                    <ManagerLink name={row.manager} />
                  </td>
                  <td className="py-2 pr-3 text-ink-tertiary">{row.games}</td>
                  <td className="py-2 pr-3">{row.actualWins}</td>
                  <td className="py-2 pr-3">{row.expectedWins}</td>
                  <td className={`py-2 pr-3 font-bold ${row.luckWins < 0 ? 'text-accent-red' : 'text-accent-gold'}`}>
                    {signed(row.luckWins)}
                  </td>
                  <td className="py-2 pr-3 text-ink-tertiary">{row.luckyWins}</td>
                  <td className="py-2 text-ink-tertiary">{row.brutalLosses}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Reveal>
      </Department>

      <div className="bg-bg-deep">
        <Department
          tag="The Curse"
          title="Win the season. Lose the jewelry."
          dek="Regular-season champions who did not win the playoff title that year, plus every 10-win season that produced zero rings. The conversion column is playoff appearances versus actual hardware."
        >
          <Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8" data-testid="bureau-curse-board">
              {curse.regularSeasonCrowns.map((row) => (
                <article key={`${row.year}-${row.manager}`} className="border border-border p-4 flex items-center gap-4">
                  <div className="font-mono text-accent-gold text-xl font-bold">{row.year}</div>
                  <ManagerAvatar name={row.manager} size="sm" />
                  <div className="min-w-0">
                    <ManagerLink name={row.manager}>
                      <div className="font-display font-black uppercase leading-none">{row.manager}</div>
                    </ManagerLink>
                    <div className="mt-1 font-mono text-[11px] text-ink-tertiary">
                      {row.record || `${row.wins}-${row.losses}`} · {row.team} · no ring
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </Reveal>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] text-left">
              <thead>
                <tr className="font-display text-[10px] tracking-[0.2em] uppercase text-ink-muted border-b border-border">
                  <th className="py-2 pr-3">Manager</th>
                  <th className="py-2 pr-3">Playoff trips</th>
                  <th className="py-2 pr-3">Rings</th>
                  <th className="py-2">Conversion</th>
                </tr>
              </thead>
              <tbody>
                {curse.conversion.map((row) => (
                  <tr key={row.manager} className="border-b border-border/60 font-mono text-sm">
                    <td className="py-2 pr-3 font-display font-black uppercase">
                      <ManagerLink name={row.manager} />
                    </td>
                    <td className="py-2 pr-3">{row.appearances}</td>
                    <td className="py-2 pr-3">{row.rings}</td>
                    <td className={`py-2 font-bold ${row.rings === 0 ? 'text-accent-red' : 'text-accent-gold'}`}>
                      {row.conversion}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Department>
      </div>

      <Department
        tag="Sacko lineage"
        title="The cursed object."
        dek="Last place in the regular-season standings, every year. Wins first, then points. The object is imaginary. The humiliation is not."
      >
        <Reveal>
          <div className="mb-6 font-display font-black uppercase text-2xl" data-testid="bureau-sacko-king">
            King: <ManagerLink name={sacko.king.manager} /> · {sacko.king.count} times
          </div>
        </Reveal>
        <Reveal>
          <ol className="space-y-2" data-testid="bureau-sacko-lineage">
            {sacko.lineage.map((row) => (
              <li key={row.year} className="grid grid-cols-[4.5rem_minmax(0,1fr)_auto] gap-3 items-center border-b border-border/50 py-2">
                <div className="font-mono text-accent-gold">{row.year}</div>
                <div className="flex items-center gap-3 min-w-0">
                  <ManagerAvatar name={row.manager} size="xs" />
                  <div className="min-w-0">
                    <ManagerLink name={row.manager}>
                      <span className="font-display font-black uppercase">{row.manager}</span>
                    </ManagerLink>
                    <div className="font-mono text-[11px] text-ink-tertiary truncate">{row.team}</div>
                  </div>
                </div>
                <div className="font-mono text-sm text-accent-red">{row.record}</div>
              </li>
            ))}
          </ol>
        </Reveal>
      </Department>

      <div className="bg-bg-deep">
        <Department
          tag="Awards show"
          title="Nobody voted. The ledger did."
          dek="Computed awards come from the same CSVs as the rest of the site. Appointed awards are labeled as such and come from public tape and team-name history, not from invented 2026 results."
        >
          <Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="bureau-awards">
              {[...awards.computed, ...awards.appointed].map((award) => (
                <article key={award.id} className="bg-bg-surface border-t-[3px] border-accent-gold p-5">
                  <div className="font-display tracking-[0.22em] text-[10px] uppercase text-accent-gold font-bold">
                    {award.title}
                  </div>
                  <div className="mt-3 flex items-center gap-3">
                    <ManagerAvatar name={award.winner} size="sm" />
                    <div>
                      <ManagerLink name={award.winner}>
                        <div className="font-display font-black uppercase text-xl leading-none">{award.winner}</div>
                      </ManagerLink>
                      <div className="mt-1 font-mono text-accent-gold">{award.value}</div>
                    </div>
                  </div>
                  <p className="mt-3 font-body italic text-sm text-ink-secondary leading-relaxed">{award.copy}</p>
                </article>
              ))}
            </div>
          </Reveal>
        </Department>
      </div>
    </>
  );
}

function HeroChip({ name, value, label }) {
  return (
    <div className="relative min-w-0 border border-accent-gold/35 bg-bg-deep/80 px-3 py-3 text-center" data-depth="0.4">
      <div className="mx-auto aspect-square w-16 overflow-hidden rounded-full border border-accent-gold/50 bg-bg-cream sm:w-20">
        <CastFigure
          name={name}
          trigger="hover"
          sequence={['shock', 'point', 'smug']}
          className="h-full w-full translate-y-[3%]"
          alt={name}
        />
      </div>
      <div className="mt-3 break-words font-display font-black uppercase leading-none text-base sm:text-lg md:text-xl">{value}</div>
      <div className="mt-1 font-mono text-[9px] tracking-[0.16em] uppercase text-ink-tertiary">{label}</div>
    </div>
  );
}
